// src/pages/SettingsPage.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getDocs,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { orgCol, getOrg } from "../utils/tenant";
import { ConfirmModal, Toast } from "../components/UIComponents";
import { useBoundStoreName } from "../hooks/useBoundStore";
import { useTheme } from "../theme/ThemeProvider";

function getStyles(themeMode) {
  const dark = themeMode === "dark";

  return {
    page: {
      padding: 0,
      maxWidth: "100%",
      margin: 0,
      background: "transparent",
      color: dark ? "#f4f7fb" : "#0f2747",
    },
    surface: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    hero: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 28,
      padding: "28px 28px 24px",
      background: dark
        ? "linear-gradient(135deg, rgba(4,18,43,0.98) 0%, rgba(3,17,42,0.98) 46%, rgba(2,10,26,0.98) 100%)"
        : "linear-gradient(135deg, rgba(245,248,253,0.98) 0%, rgba(239,244,251,0.98) 45%, rgba(232,239,249,0.98) 100%)",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.28)"
        : "1px solid rgba(150, 174, 205, 0.28)",
      boxShadow: dark
        ? "0 24px 60px rgba(0,0,0,0.34)"
        : "0 20px 45px rgba(24, 55, 96, 0.08)",
    },
    heroGlow: {
      position: "absolute",
      inset: "auto auto -90px 30%",
      width: 260,
      height: 260,
      borderRadius: "50%",
      background: dark
        ? "radial-gradient(circle, rgba(65,147,255,0.22) 0%, rgba(65,147,255,0.08) 38%, rgba(65,147,255,0) 72%)"
        : "radial-gradient(circle, rgba(72,144,255,0.16) 0%, rgba(72,144,255,0.06) 38%, rgba(72,144,255,0) 72%)",
      pointerEvents: "none",
      filter: "blur(10px)",
    },
    badgeRow: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 18,
      flexWrap: "wrap",
      zIndex: 1,
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 14px",
      borderRadius: 999,
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.35)"
        : "1px solid rgba(150, 174, 205, 0.4)",
      background: dark ? "rgba(10, 29, 61, 0.72)" : "rgba(255,255,255,0.66)",
      color: dark ? "#74b3ff" : "#2f6ff2",
      fontSize: 12,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.14em",
    },
    topRow: {
      position: "relative",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 18,
      flexWrap: "wrap",
      zIndex: 1,
    },
    heroLeft: {
      minWidth: 260,
      flex: "1 1 420px",
    },
    heroRight: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 8,
      minWidth: 180,
      marginLeft: "auto",
    },
    title: {
      margin: 0,
      fontSize: "clamp(2rem, 4vw, 3rem)",
      lineHeight: 1.04,
      fontWeight: 900,
      color: dark ? "#f4f7fb" : "#112c53",
    },
    titleAccent: {
      color: "#4f9cff",
      marginLeft: 10,
    },
    subtitle: {
      marginTop: 10,
      color: dark ? "rgba(223,233,248,0.78)" : "#607897",
      fontSize: 16,
      maxWidth: 640,
    },
    liveTime: {
      fontSize: "clamp(2rem, 4vw, 3.3rem)",
      lineHeight: 0.95,
      fontWeight: 900,
      color: dark ? "#f4f7fb" : "#12335f",
      letterSpacing: "-0.04em",
    },
    liveSub: {
      color: dark ? "rgba(223,233,248,0.78)" : "#607897",
      fontSize: 14,
      textAlign: "right",
    },
    onlinePill: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 14px",
      borderRadius: 999,
      background: dark ? "rgba(25, 163, 116, 0.16)" : "rgba(63, 194, 135, 0.16)",
      border: "1px solid rgba(63, 194, 135, 0.25)",
      color: "#17a56e",
      fontWeight: 800,
      fontSize: 13,
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
      gap: 16,
    },
    statCard: {
      borderRadius: 24,
      padding: "20px 20px 18px",
      background: dark
        ? "linear-gradient(180deg, rgba(7,23,47,0.98) 0%, rgba(6,20,41,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,255,0.98) 100%)",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.18)"
        : "1px solid rgba(150, 174, 205, 0.22)",
      boxShadow: dark
        ? "0 20px 40px rgba(0,0,0,0.22)"
        : "0 16px 36px rgba(24, 55, 96, 0.08)",
    },
    statLabel: {
      fontSize: 12,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.12em",
      color: dark ? "#89aedf" : "#6d83a2",
      marginBottom: 12,
    },
    statValue: {
      fontSize: 30,
      fontWeight: 900,
      lineHeight: 1,
      color: dark ? "#f4f7fb" : "#0f2747",
      marginBottom: 8,
    },
    statSub: {
      color: dark ? "rgba(223,233,248,0.74)" : "#6c84a1",
      fontSize: 14,
    },
    gridScroll: {
      overflowX: "auto",
      paddingBottom: 2,
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "1.2fr 1fr",
      gap: 16,
      alignItems: "start",
      minWidth: 980,
    },
    stack: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    panel: {
      borderRadius: 28,
      overflow: "hidden",
      background: dark
        ? "linear-gradient(180deg, rgba(6,21,44,0.98) 0%, rgba(4,15,33,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,250,254,0.98) 100%)",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.2)"
        : "1px solid rgba(150, 174, 205, 0.2)",
      boxShadow: dark
        ? "0 24px 50px rgba(0,0,0,0.26)"
        : "0 18px 40px rgba(24, 55, 96, 0.08)",
    },
    panelHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      padding: "22px 22px 18px",
      borderBottom: dark
        ? "1px solid rgba(67, 113, 181, 0.16)"
        : "1px solid rgba(150, 174, 205, 0.18)",
      flexWrap: "wrap",
    },
    panelTitle: {
      fontSize: 24,
      fontWeight: 900,
      color: dark ? "#f4f7fb" : "#112c53",
      margin: 0,
    },
    panelSub: {
      marginTop: 6,
      color: dark ? "rgba(223,233,248,0.72)" : "#6c84a1",
      fontSize: 14,
    },
    panelBody: {
      padding: 20,
    },
    rowGrid: {
      display: "grid",
      gridTemplateColumns: "140px 1fr",
      rowGap: 10,
      columnGap: 14,
      color: dark ? "#e4eefb" : "#173960",
      fontSize: 14,
    },
    rowLabel: {
      color: dark ? "#9eb6d0" : "#6d83a2",
      fontWeight: 700,
    },
    note: {
      marginTop: 12,
      fontSize: 13,
      color: dark ? "#9eb6d0" : "#6d83a2",
      lineHeight: 1.5,
    },
    toggleWrap: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
    },
    switchRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      cursor: "pointer",
      color: dark ? "#e4eefb" : "#173960",
      fontSize: 14,
    },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 12px",
      borderRadius: 999,
      background: dark ? "rgba(10, 29, 61, 0.76)" : "rgba(255,255,255,0.8)",
      border: dark ? "1px solid rgba(67,113,181,0.24)" : "1px solid rgba(150,174,205,0.24)",
      color: dark ? "#dbe7fb" : "#173960",
      fontSize: 13,
      fontWeight: 700,
    },
    themePills: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
    },
    controlGroup: {
      marginTop: 20,
    },
    controlRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
      marginTop: 10,
    },
    smallInput: {
      width: 110,
      padding: 12,
      fontSize: 16,
      borderRadius: 14,
      border: dark ? "1px solid rgba(67,113,181,0.24)" : "1px solid rgba(150,174,205,0.28)",
      background: dark ? "rgba(10,29,61,0.76)" : "rgba(255,255,255,0.88)",
      color: dark ? "#eff5ff" : "#173960",
      outline: "none",
      boxSizing: "border-box",
    },
    smallButton: {
      background: "linear-gradient(135deg, rgba(31,111,224,0.98) 0%, rgba(79,156,255,0.98) 100%)",
      color: "#fff",
      border: "none",
      padding: "12px 16px",
      borderRadius: 14,
      cursor: "pointer",
      fontWeight: 800,
      boxShadow: "0 10px 20px rgba(31,111,224,0.14)",
    },
    themeButton: (active, accent = false) => ({
      padding: "10px 14px",
      borderRadius: 999,
      border: active
        ? "1px solid rgba(79,156,255,0.28)"
        : dark
        ? "1px solid rgba(67,113,181,0.24)"
        : "1px solid rgba(150,174,205,0.24)",
      cursor: "pointer",
      background: active
        ? accent
          ? "linear-gradient(135deg, rgba(22,163,116,0.96) 0%, rgba(49,198,145,0.96) 100%)"
          : "linear-gradient(135deg, rgba(31,111,224,0.98) 0%, rgba(79,156,255,0.98) 100%)"
        : dark
        ? "rgba(10,29,61,0.76)"
        : "rgba(255,255,255,0.82)",
      color: active ? "#fff" : dark ? "#dbe7fb" : "#173960",
      fontWeight: 800,
      boxShadow: active ? "0 10px 22px rgba(31,111,224,0.14)" : "none",
    }),
    accordionBtn: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "18px 20px",
      textAlign: "left",
      background: dark
        ? "linear-gradient(180deg, rgba(6,21,44,0.98) 0%, rgba(4,15,33,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,255,0.98) 100%)",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.2)"
        : "1px solid rgba(150, 174, 205, 0.22)",
      borderRadius: 20,
      fontWeight: 900,
      fontSize: 18,
      color: dark ? "#f4f7fb" : "#112c53",
      cursor: "pointer",
      boxShadow: dark
        ? "0 24px 50px rgba(0,0,0,0.26)"
        : "0 16px 36px rgba(24, 55, 96, 0.08)",
    },
    accordionBtnActive: {
      background: dark
        ? "linear-gradient(180deg, rgba(9,40,33,0.96) 0%, rgba(6,31,25,0.96) 100%)"
        : "linear-gradient(180deg, rgba(232,249,240,0.98) 0%, rgba(241,252,246,0.98) 100%)",
      border: "1px solid rgba(63, 194, 135, 0.22)",
    },
    accordionBody: {
      padding: 18,
      borderRadius: 24,
      background: dark
        ? "linear-gradient(180deg, rgba(6,21,44,0.98) 0%, rgba(4,15,33,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,250,254,0.98) 100%)",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.2)"
        : "1px solid rgba(150, 174, 205, 0.2)",
      boxShadow: dark
        ? "0 24px 50px rgba(0,0,0,0.26)"
        : "0 18px 40px rgba(24, 55, 96, 0.08)",
    },
    input: {
      width: "100%",
      padding: 12,
      fontSize: 16,
      borderRadius: 14,
      border: dark ? "1px solid rgba(67,113,181,0.24)" : "1px solid rgba(150,174,205,0.28)",
      marginBottom: 12,
      background: dark ? "rgba(10,29,61,0.76)" : "rgba(255,255,255,0.88)",
      color: dark ? "#eff5ff" : "#173960",
      outline: "none",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: 12,
      fontSize: 16,
      borderRadius: 14,
      border: dark ? "1px solid rgba(67,113,181,0.24)" : "1px solid rgba(150,174,205,0.28)",
      marginBottom: 12,
      background: dark ? "rgba(10,29,61,0.76)" : "rgba(255,255,255,0.88)",
      color: dark ? "#eff5ff" : "#173960",
      outline: "none",
      boxSizing: "border-box",
    },
    primary: {
      background: "linear-gradient(135deg, rgba(22,163,116,0.96) 0%, rgba(49,198,145,0.96) 100%)",
      color: "#fff",
      border: "none",
      padding: "14px 16px",
      width: "100%",
      borderRadius: 16,
      cursor: "pointer",
      fontWeight: 800,
      boxShadow: "0 12px 24px rgba(49,198,145,0.16)",
    },
    unlinkBtn: {
      background: "linear-gradient(135deg, rgba(195,46,75,0.96) 0%, rgba(236,87,87,0.96) 100%)",
      color: "#fff",
      border: "none",
      padding: "16px 18px",
      width: "100%",
      borderRadius: 20,
      cursor: "pointer",
      fontWeight: 900,
      boxShadow: "0 14px 28px rgba(220,53,69,0.16)",
    },
    backBtn: {
      background: dark
        ? "linear-gradient(135deg, rgba(8,30,61,0.98) 0%, rgba(13,52,100,0.98) 100%)"
        : "linear-gradient(135deg, rgba(230,239,251,0.98) 0%, rgba(218,232,250,0.98) 100%)",
      color: dark ? "#f4f7fb" : "#12335f",
      border: dark ? "1px solid rgba(67, 113, 181, 0.2)" : "1px solid rgba(150, 174, 205, 0.22)",
      padding: "18px 18px",
      width: "100%",
      borderRadius: 20,
      cursor: "pointer",
      fontWeight: 900,
      fontSize: 20,
      textAlign: "left",
      boxShadow: dark ? "0 18px 38px rgba(0,0,0,0.22)" : "0 16px 34px rgba(24, 55, 96, 0.08)",
    },
    tableWrap: { overflowX: "auto" },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      borderRadius: 18,
      overflow: "hidden",
    },
    th: {
      background: dark ? "rgba(11,34,68,0.72)" : "#f2f6fb",
      textAlign: "left",
      padding: "12px 14px",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.14)" : "1px solid #e1e7ef",
      fontWeight: 800,
      fontSize: 12,
      color: dark ? "#8db4e6" : "#0b3b8c",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    },
    td: {
      padding: "14px",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.08)" : "1px solid #eef2f5",
      fontSize: 14,
      color: dark ? "#eff5ff" : "#173960",
      verticalAlign: "middle",
      wordBreak: "break-word",
    },
    trAlt: { background: dark ? "rgba(10,29,61,0.26)" : "#fcfdff" },
    actionsCell: { width: 110, textAlign: "right", whiteSpace: "nowrap" },
    editBtn: {
      padding: "8px 12px",
      background: "linear-gradient(135deg, rgba(31,111,224,0.98) 0%, rgba(79,156,255,0.98) 100%)",
      color: "#fff",
      border: "none",
      borderRadius: 10,
      cursor: "pointer",
      fontWeight: 700,
      boxShadow: "0 10px 20px rgba(31,111,224,0.14)",
    },
    arrow: {
      fontSize: 18,
      opacity: 0.8,
    },
  };
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { themeMode, setThemeMode } = useTheme();
  const styles = useMemo(() => getStyles(themeMode), [themeMode]);
  const boundStoreName = useBoundStoreName();

  const storeId = localStorage.getItem("storeId") || "";
  const storeName =
    boundStoreName ||
    localStorage.getItem("storeName") ||
    localStorage.getItem("storeLabel") ||
    "Store";

  const { orgId, orgName: storedOrgName = "" } = useMemo(() => getOrg() || { orgId: "", orgName: "" }, []);

  const [orgName, setOrgName] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [resetStep, setResetStep] = useState(0);
  const [resetText, setResetText] = useState("");
  const [staffUploadsEnabled, setStaffUploadsEnabled] = useState(true);
  const [savingUploadsToggle, setSavingUploadsToggle] = useState(false);
  const [monthlyTargetHours, setMonthlyTargetHours] = useState(180);
  const [savingMonthlyTarget, setSavingMonthlyTarget] = useState(false);
  const [abandonedShiftHours, setAbandonedShiftHours] = useState(18);
  const [savingAbandonedShiftHours, setSavingAbandonedShiftHours] = useState(false);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [roleId, setRoleId] = useState("");
  const [toast, setToast] = useState({ message: "", type: "info" });
  const [errorMsg, setErrorMsg] = useState("");
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      const installParams = (() => {
        try {
          return JSON.parse(localStorage.getItem("installParams") || "{}");
        } catch {
          return {};
        }
      })();

      const fallbackOrgName =
        storedOrgName ||
        localStorage.getItem("orgName") ||
        localStorage.getItem("orgLabel") ||
        installParams?.orgName ||
        sessionStorage.getItem("orgName") ||
        sessionStorage.getItem("orgLabel") ||
        "";

      if (!orgId) {
        setOrgName("");
        return;
      }

      if (fallbackOrgName) {
        setOrgName(String(fallbackOrgName));
      }

      try {
        const snap = await getDoc(doc(db, "orgs", orgId));
        if (!snap.exists()) {
          setOrgName(String(fallbackOrgName || orgId));
          return;
        }

        const data = snap.data() || {};
        const resolvedOrgName =
          data.name ||
          data.label ||
          data.orgName ||
          data.displayName ||
          data.title ||
          data.orgLabel ||
          data.companyName ||
          fallbackOrgName ||
          orgId;

        const finalName = String(resolvedOrgName || orgId);
        setOrgName(finalName);
        if (finalName && finalName !== orgId) {
          localStorage.setItem("orgName", finalName);
          sessionStorage.setItem("orgName", finalName);
        }
      } catch {
        setOrgName(String(fallbackOrgName || orgId));
      }
    })();
  }, [orgId, storedOrgName]);

  const loadAll = useCallback(async () => {
    if (!orgId || !storeId) return;
    try {
      try {
        const [storeSnap, laborSnap] = await Promise.all([
          getDoc(doc(db, "orgs", orgId, "stores", storeId)),
          getDoc(doc(db, "orgs", orgId, "settings", "labor")),
        ]);
        const storeData = storeSnap.exists() ? storeSnap.data() || {} : {};
        const laborData = laborSnap.exists() ? laborSnap.data() || {} : {};
        const v = storeData?.staffUploadsEnabled;
        const target = Number(storeData?.monthlyTargetHours);
        const rawAbandonedShiftHours = Number(
          storeData?.abandonedShiftHours ?? laborData?.abandonedShiftHours
        );
        setStaffUploadsEnabled(typeof v === "boolean" ? v : true);
        setMonthlyTargetHours(Number.isFinite(target) && target > 0 ? Math.round(target) : 180);
        setAbandonedShiftHours(
          Number.isFinite(rawAbandonedShiftHours)
            ? Math.min(24, Math.max(18, Math.round(rawAbandonedShiftHours)))
            : 18
        );
      } catch {
        setStaffUploadsEnabled(true);
        setMonthlyTargetHours(180);
        setAbandonedShiftHours(18);
      }

      const usersRef = orgCol(db, "cashup_users");
      const qUsers = query(usersRef, where("storeId", "==", storeId));
      const uSnap = await getDocs(qUsers);
      const users = uSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => !(u?.archived === true || u?.isArchived === true));
      users.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setStaffList(users);

      const rSnap = await getDocs(orgCol(db, "cashup_roles"));
      const rs = rSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => !(r?.archived === true || r?.isArchived === true));
      rs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setRoles(rs);
    } catch (e) {
      setErrorMsg(e.message || "Failed to load settings.");
    }
  }, [orgId, storeId]);

  const saveUploadsToggle = async (nextVal) => {
    if (!orgId || !storeId) return;
    setSavingUploadsToggle(true);
    try {
      const storeRef = doc(db, "orgs", orgId, "stores", storeId);
      await setDoc(
        storeRef,
        {
          staffUploadsEnabled: !!nextVal,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setStaffUploadsEnabled(!!nextVal);
      setToast({
        message: nextVal ? "✔️ Staff uploads enabled." : "✔️ Staff uploads disabled.",
        type: "success",
      });
    } catch (e) {
      setToast({ message: e.message || "Failed to save setting.", type: "error" });
    } finally {
      setSavingUploadsToggle(false);
    }
  };



  const saveMonthlyTarget = async () => {
    if (!orgId || !storeId) return;
    const safeTarget = Math.max(1, Math.min(400, Math.round(Number(monthlyTargetHours) || 180)));
    setSavingMonthlyTarget(true);
    try {
      const storeRef = doc(db, "orgs", orgId, "stores", storeId);
      await setDoc(
        storeRef,
        {
          monthlyTargetHours: safeTarget,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setMonthlyTargetHours(safeTarget);
      setToast({
        message: `✔️ Monthly target saved at ${safeTarget} hours.`,
        type: "success",
      });
    } catch (e) {
      setToast({ message: e.message || "Failed to save monthly target.", type: "error" });
    } finally {
      setSavingMonthlyTarget(false);
    }
  };

  const saveAbandonedShiftThreshold = async () => {
    if (!orgId || !storeId) return;
    const safeHours = Math.min(24, Math.max(18, Math.round(Number(abandonedShiftHours) || 18)));
    setSavingAbandonedShiftHours(true);
    try {
      await setDoc(
        doc(db, "orgs", orgId, "settings", "labor"),
        {
          abandonedShiftHours: safeHours,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await setDoc(
        doc(db, "orgs", orgId, "stores", storeId),
        {
          abandonedShiftHours: safeHours,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setAbandonedShiftHours(safeHours);
      setToast({
        message: `✔️ Force auto clock-out set to ${safeHours} hours.`,
        type: "success",
      });
    } catch (e) {
      setToast({ message: e.message || "Failed to save force auto clock-out.", type: "error" });
    } finally {
      setSavingAbandonedShiftHours(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!toast.message) return;
    const t = setTimeout(() => setToast({ message: "", type: "info" }), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const toggle = (sec) => {
    setActiveSection((prev) => (prev === sec ? null : sec));
    setEditingId(null);
    setName("");
    setPin("");
    setRoleId("");
    setErrorMsg("");
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setName(s.name || "");
    setPin(String(s.pin || ""));
    const rid = s.roleId || roles.find((r) => r.name === s.role)?.id || "";
    setRoleId(rid);
    setActiveSection("add");
  };

  const onPinChange = (e) => {
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
    setPin(digits);
  };

  const handleSaveStaff = async () => {
    if (!name.trim() || !pin.trim() || !roleId.trim()) {
      setToast({ message: "Please fill Name, PIN & Role.", type: "info" });
      return;
    }
    try {
      const roleObj = roles.find((r) => r.id === roleId);
      const payload = {
        name: name.trim(),
        pin: pin.trim(),
        roleId,
        role: roleObj?.name || "",
        storeId,
      };

      if (editingId) {
        await updateDoc(doc(orgCol(db, "cashup_users"), editingId), payload);
        setToast({ message: "✔️ Staff updated.", type: "success" });
      } else {
        await addDoc(orgCol(db, "cashup_users"), payload);
        setToast({ message: "✔️ Staff added.", type: "success" });
      }

      setEditingId(null);
      await loadAll();
      setActiveSection("current");
      setName("");
      setPin("");
      setRoleId("");
    } catch (e) {
      setErrorMsg(e.message || "Failed to save staff.");
    }
  };

  const beginReset = () => setResetStep(1);
  const cancelReset = () => {
    setResetStep(0);
    setResetText("");
  };
  const nextReset = () => setResetStep(2);

  const performReset = async () => {
    if (resetText !== storeName) {
      setToast({
        message: `✗ Input must exactly match "${storeName}"`,
        type: "error",
      });
      return;
    }

    try {
      try {
        const fns = getFunctions(undefined, "us-central1");
        const unlinkCurrentKioskDevice = httpsCallable(fns, "unlinkCurrentKioskDevice");
        await unlinkCurrentKioskDevice({});
      } catch (e) {
        console.warn("unlinkCurrentKioskDevice failed", e);
      }

      const keys = [
        "orgId",
        "orgSlug",
        "storeId",
        "storeName",
        "storeLabel",
        "orgName",
        "deviceUid",
        "managerId",
        "managerName",
        "staffName",
        "staffId",
        "installParams",
        "linkedStoreId",
      ];
      keys.forEach((k) => localStorage.removeItem(k));
      localStorage.setItem("neo.relinkRequired", "1");
      sessionStorage.clear();
      setResetStep(0);
      setResetText("");
      try { sessionStorage.setItem("neo.allowDeviceReset", "1"); } catch {}
      window.location.replace("/link?reset=1");
    } catch (err) {
      console.error("Unlink failed:", err);
      setToast({ message: "✗ Unlink failed", type: "error" });
    }
  };

  const displayRoleName = (s) =>
    roles.find((r) => r.id === s.roleId)?.name ||
    roles.find((r) => r.name === s.role)?.name ||
    s.role ||
    "—";

  const timeText = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const dateText = now.toLocaleDateString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="page" style={styles.page}>
      <div style={styles.surface}>
        <div style={styles.hero}>
          <div style={styles.heroGlow} />
          <div style={styles.badgeRow}>
            <div style={styles.badge}>Neo Manager Settings</div>
          </div>

          <div style={styles.topRow}>
            <div style={styles.heroLeft}>
              <h1 style={styles.title}>
                Settings
                <span style={styles.titleAccent}>{storeName}</span>
              </h1>
              <div style={styles.subtitle}>
                Configure device binding, staff management, uploads, and appearance for this store.
              </div>
            </div>

            <div style={styles.heroRight}>
              <div style={styles.liveTime}>{timeText}</div>
              <div style={styles.liveSub}>{dateText}</div>
              <div style={styles.onlinePill}>Manager control</div>
            </div>
          </div>
        </div>

        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Store</div>
            <div style={styles.statValue}>{storeName}</div>
            <div style={styles.statSub}>Current device binding</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Org</div>
            <div style={styles.statValue}>{orgName || orgId || "—"}</div>
            <div style={styles.statSub}>Tenant currently in use</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Staff profiles</div>
            <div style={styles.statValue}>{staffList.length}</div>
            <div style={styles.statSub}>Staff saved for this store</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Uploads</div>
            <div style={styles.statValue}>{staffUploadsEnabled ? "On" : "Off"}</div>
            <div style={styles.statSub}>Gallery / file uploads for staff</div>
          </div>
        </div>

        <div style={styles.gridScroll}><div style={styles.grid}>
          <div style={styles.stack}>
            <div style={styles.panel}>
              <div style={styles.panelHeader}>
                <div>
                  <h2 style={styles.panelTitle}>Device status</h2>
                  <div style={styles.panelSub}>Current device binding and store identity.</div>
                </div>
              </div>
              <div style={styles.panelBody}>
                <div style={styles.rowGrid}>
                  <div style={styles.rowLabel}>Org</div>
                  <div>{orgName || orgId || "—"}</div>
                  <div style={styles.rowLabel}>Store ID</div>
                  <div>{storeId || "—"}</div>
                  <div style={styles.rowLabel}>Store name</div>
                  <div>{storeName || "—"}</div>
                </div>
                <div style={styles.note}>
                  This device is currently linked to the store above. Unlinking only clears the local binding on this device and does not delete store data.
                </div>
              </div>
            </div>

            <div style={styles.panel}>
              <div style={styles.panelHeader}>
                <div>
                  <h2 style={styles.panelTitle}>Store controls</h2>
                  <div style={styles.panelSub}>Photo uploads and appearance are managed here.</div>
                </div>
              </div>
              <div style={styles.panelBody}>
                <div style={styles.toggleWrap}>
                  <label style={styles.switchRow}>
                    <input
                      type="checkbox"
                      checked={!!staffUploadsEnabled}
                      disabled={savingUploadsToggle || !orgId || !storeId}
                      onChange={(e) => saveUploadsToggle(e.target.checked)}
                    />
                    <span>
                      Allow staff to <b>upload</b> photos from gallery/files
                    </span>
                  </label>
                  <span style={styles.chip}>Take photo (camera) stays available</span>
                </div>

                <div style={styles.controlGroup}>
                  <div style={styles.panelSub}>Staff Portal Monthly Target</div>
                  <div style={styles.controlRow}>
                    <input
                      type="number"
                      min="1"
                      max="400"
                      step="1"
                      value={monthlyTargetHours}
                      onChange={(e) => setMonthlyTargetHours(e.target.value)}
                      style={styles.smallInput}
                    />
                    <button
                      type="button"
                      onClick={saveMonthlyTarget}
                      disabled={savingMonthlyTarget || !orgId || !storeId}
                      style={{
                        ...styles.smallButton,
                        opacity: savingMonthlyTarget || !orgId || !storeId ? 0.65 : 1,
                        cursor: savingMonthlyTarget || !orgId || !storeId ? "not-allowed" : "pointer",
                      }}
                    >
                      {savingMonthlyTarget ? "Saving..." : "Save target"}
                    </button>
                    <span style={styles.chip}>Used in Staff Portal progress</span>
                  </div>
                  <div style={styles.note}>
                    Set the monthly target hours for staff. Once reached, the portal will show Target met and count extra time as overtime.
                  </div>
                </div>

                <div style={styles.controlGroup}>
                  <div style={styles.panelSub}>Force auto clock-out (hours)</div>
                  <div style={styles.controlRow}>
                    <input
                      type="number"
                      min="18"
                      max="24"
                      step="1"
                      value={abandonedShiftHours}
                      onChange={(e) => setAbandonedShiftHours(e.target.value)}
                      style={styles.smallInput}
                    />
                    <button
                      type="button"
                      onClick={saveAbandonedShiftThreshold}
                      disabled={savingAbandonedShiftHours || !orgId || !storeId}
                      style={{
                        ...styles.smallButton,
                        opacity: savingAbandonedShiftHours || !orgId || !storeId ? 0.65 : 1,
                        cursor: savingAbandonedShiftHours || !orgId || !storeId ? "not-allowed" : "pointer",
                      }}
                    >
                      {savingAbandonedShiftHours ? "Saving..." : "Save threshold"}
                    </button>
                    <span style={styles.chip}>Minimum 18 • Maximum 24</span>
                  </div>
                  <div style={styles.note}>
                    Open shifts are force-closed at this threshold so staff do not remain under Active Staff. Shifts are no longer cut at 23:59 and may continue into the next day.
                  </div>
                </div>

                <div style={{ ...styles.panelSub, marginTop: 20 }}>Appearance</div>
                <div style={styles.themePills}>
                  <button
                    type="button"
                    onClick={() => setThemeMode("light")}
                    style={styles.themeButton(themeMode === "light")}
                  >
                    Light
                  </button>

                  <button
                    type="button"
                    onClick={() => setThemeMode("dark")}
                    style={styles.themeButton(themeMode === "dark")}
                  >
                    Dark
                  </button>                
                </div>
                <div style={styles.note}>
                  This controls the shared app-wide light and dark theme used across the redesigned Neo pages.
                </div>
              </div>
            </div>
          </div>

          <div style={styles.stack}>
            <button
              style={{
                ...styles.accordionBtn,
                ...(activeSection === "current" ? styles.accordionBtnActive : {}),
              }}
              onClick={() => toggle("current")}
            >
              <span>Current Staff</span>
              <span style={styles.arrow}>{activeSection === "current" ? "−" : "+"}</span>
            </button>

            {activeSection === "current" && (
              <div style={styles.accordionBody}>
                {!staffList.length ? (
                  <em>No staff found.</em>
                ) : (
                  <div style={styles.tableWrap}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Name</th>
                          <th style={styles.th}>Role</th>
                          <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staffList.map((s, idx) => (
                          <tr key={s.id} style={idx % 2 ? styles.trAlt : undefined}>
                            <td style={styles.td}>{s.name || "—"}</td>
                            <td style={styles.td}>{displayRoleName(s)}</td>
                            <td style={{ ...styles.td, ...styles.actionsCell }}>
                              <button style={styles.editBtn} onClick={() => startEdit(s)}>
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div style={styles.note}>To remove staff members, please use the Admin panel.</div>
              </div>
            )}

            <button
              style={{
                ...styles.accordionBtn,
                ...(activeSection === "add" ? styles.accordionBtnActive : {}),
              }}
              onClick={() => toggle("add")}
            >
              <span>{editingId ? "Edit Staff" : "Add Staff"}</span>
              <span style={styles.arrow}>{activeSection === "add" ? "−" : "+"}</span>
            </button>

            {activeSection === "add" && (
              <div style={styles.accordionBody}>
                <input
                  style={styles.input}
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />

                <input
                  style={{ ...styles.input, WebkitTextSecurity: "disc" }}
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="PIN"
                  value={pin}
                  onChange={onPinChange}
                  autoComplete="off"
                />

                <select
                  style={styles.select}
                  value={roleId}
                  onChange={(e) => setRoleId(e.target.value)}
                >
                  <option value="">Select Role</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>

                <button style={styles.primary} onClick={handleSaveStaff}>
                  {editingId ? "Update Staff" : "Save Staff"}
                </button>

                <div style={styles.note}>
                  Staff will be saved with the current store ID on this device.
                </div>
              </div>
            )}
          </div>
        </div></div>

        <button style={styles.unlinkBtn} onClick={beginReset}>
          Unlink this device from the store
        </button>

        <button style={styles.backBtn} onClick={() => navigate("/manager-dashboard") }>
          ← Back
        </button>
      </div>

      <ConfirmModal
        isOpen={resetStep === 1}
        message="Are you sure you want to unlink this device from the current store?"
        onConfirm={nextReset}
        onCancel={cancelReset}
      />

      <ConfirmModal
        isOpen={resetStep === 2}
        message={`Type "${storeName}" to confirm unlink:`}
        onConfirm={performReset}
        onCancel={cancelReset}
      >
        <input
          style={styles.input}
          placeholder="Exact store name"
          value={resetText}
          onChange={(e) => setResetText(e.target.value)}
        />
      </ConfirmModal>

      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "info" })}
      />
      {errorMsg && (
        <ConfirmModal
          isOpen={!!errorMsg}
          message={errorMsg}
          onConfirm={() => setErrorMsg("")}
          onCancel={() => setErrorMsg("")}
        />
      )}
    </div>
  );
}
